import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CheckCircle, Circle, Receipt, Filter } from "lucide-react";
import Modal from "../components/Modal";
import SummaryCard from "../components/SummaryCard";
import { getBills, createBill, updateBill, toggleBillPaid, deleteBill } from "../utils/api";
import { toMonthly, calcMonthlyBills, fmt } from "../utils/calculations";

const CATEGORIES = [
  "All","Housing","Utilities","Subscriptions","Transportation",
  "Groceries","Insurance","Entertainment","Medical","Other",
];

const FREQ_OPTS = [
  { value: "weekly",   label: "Weekly" },
  { value: "monthly",  label: "Monthly" },
  { value: "annual",   label: "Annual" },
  { value: "one-time", label: "One-time" },
];

const CATEGORY_COLORS = {
  Housing:       "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
  Utilities:     "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400",
  Subscriptions: "bg-pink-100   dark:bg-pink-900/30   text-pink-700   dark:text-pink-400",
  Transportation:"bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
  Groceries:     "bg-green-100  dark:bg-green-900/30  text-green-700  dark:text-green-400",
  Insurance:     "bg-teal-100   dark:bg-teal-900/30   text-teal-700   dark:text-teal-400",
  Entertainment: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400",
  Medical:       "bg-red-100    dark:bg-red-900/30    text-red-700    dark:text-red-400",
  Other:         "bg-gray-100   dark:bg-gray-700       text-gray-700   dark:text-gray-300",
};

const EMPTY = { name: "", amount: "", dueDate: "", frequency: "monthly", category: "Housing" };

export default function Bills() {
  const [bills,         setBills]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterCat,     setFilterCat]     = useState("All");
  const [filterPaid,    setFilterPaid]    = useState("all");
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try { setBills(await getBills()); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openAdd() { setEditing(null); setForm(EMPTY); setError(""); setModalOpen(true); }

  function openEdit(b) {
    setEditing(b);
    setForm({ name: b.name || "", amount: b.amount?.toString() || "", dueDate: b.dueDate || "", frequency: b.frequency || "monthly", category: b.category || "Housing" });
    setError(""); setModalOpen(true);
  }

  function handleChange(e) { setForm((f) => ({ ...f, [e.target.name]: e.target.value })); }

  async function handleSave(e) {
    e.preventDefault(); setError("");
    if (!form.name || !form.amount || !form.category) { setError("Name, amount, and category are required."); return; }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateBill(editing.id, form);
        setBills((prev) => prev.map((b) => (b.id === editing.id ? { ...b, ...updated } : b)));
      } else {
        const created = await createBill(form);
        setBills((prev) => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleTogglePaid(id) {
    try {
      const updated = await toggleBillPaid(id);
      setBills((prev) => prev.map((b) => (b.id === id ? { ...b, isPaid: updated.isPaid, lastPaidDate: updated.lastPaidDate } : b)));
    } catch (err) { alert(err.message); }
  }

  async function handleDelete(id) {
    try { await deleteBill(id); setBills((prev) => prev.filter((b) => b.id !== id)); }
    catch (err) { alert(err.message); }
    finally { setDeleteConfirm(null); }
  }

  const filtered = bills.filter((b) => {
    const catOk  = filterCat  === "All"    || b.category === filterCat;
    const paidOk = filterPaid === "all"    || (filterPaid === "paid" ? b.isPaid : !b.isPaid);
    return catOk && paidOk;
  });

  const monthlyTotal = calcMonthlyBills(bills);
  const paidCount    = bills.filter((b) => b.isPaid).length;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bills & Expenses</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage recurring and one-time bills</p>
        </div>
        <button onClick={openAdd} className="btn-primary"><Plus className="w-4 h-4" /> Add Bill</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard title="Total Monthly" value={fmt(monthlyTotal)} icon={Receipt} color="yellow" />
        <SummaryCard title="Annual Total" value={fmt(monthlyTotal * 12)} subtitle="estimated" icon={Receipt} color="blue" />
        <SummaryCard title="Bills" value={bills.length} subtitle="tracked" icon={Receipt} color="purple" />
        <SummaryCard title="Paid This Month" value={`${paidCount} / ${bills.filter(b => b.frequency !== "one-time").length}`} subtitle="recurring" icon={CheckCircle} color="green" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filterCat === c
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}>{c}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          {["all","unpaid","paid"].map((v) => (
            <button key={v} onClick={() => setFilterPaid(v)}
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                filterPaid === v
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}>{v}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <Receipt className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
            {bills.length === 0 ? "No bills yet" : "No bills match the filters"}
          </h3>
          {bills.length === 0 && (
            <button onClick={openAdd} className="btn-primary inline-flex mt-2"><Plus className="w-4 h-4" /> Add Bill</button>
          )}
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="px-5 py-3 w-8" />
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Frequency</th>
                <th className="px-5 py-3">Due Date</th>
                <th className="px-5 py-3 text-right">Monthly</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((b) => (
                <tr key={b.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${b.isPaid ? "opacity-60" : ""}`}>
                  <td className="px-5 py-3.5">
                    <button onClick={() => handleTogglePaid(b.id)} title={b.isPaid ? "Mark unpaid" : "Mark paid"} className="transition-colors">
                      {b.isPaid
                        ? <CheckCircle className="w-5 h-5 text-secondary-500" />
                        : <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600 hover:text-secondary-400" />
                      }
                    </button>
                  </td>
                  <td className={`px-5 py-3.5 font-medium ${b.isPaid ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-gray-100"}`}>{b.name}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[b.category] || CATEGORY_COLORS.Other}`}>
                      {b.category}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-gray-800 dark:text-gray-200">{fmt(b.amount)}</td>
                  <td className="px-5 py-3.5 text-gray-600 dark:text-gray-400 capitalize">{b.frequency}</td>
                  <td className="px-5 py-3.5 text-gray-600 dark:text-gray-400">
                    {b.dueDate ? new Date(b.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-right font-semibold text-red-700 dark:text-red-400">
                    {fmt(toMonthly(b.amount, b.frequency))}/mo
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(b)} className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirm(b.id)} className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                <td colSpan={6} className="px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Monthly Total ({filtered.length} of {bills.length})
                </td>
                <td className="px-5 py-3 text-right text-sm font-bold text-red-700 dark:text-red-400">
                  {fmt(calcMonthlyBills(filtered))}/mo
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Bill" : "Add Bill"}>
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Bill name *</label>
              <input className="input" name="name" placeholder="e.g. Rent" value={form.name} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Amount *</label>
              <input className="input" type="number" name="amount" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Category *</label>
              <select className="input" name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.slice(1).map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Frequency *</label>
              <select className="input" name="frequency" value={form.frequency} onChange={handleChange}>
                {FREQ_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Due date</label>
              <input className="input" type="date" name="dueDate" value={form.dueDate} onChange={handleChange} />
            </div>
          </div>
          {form.amount && form.frequency && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
              Monthly equivalent: <strong>{fmt(toMonthly(parseFloat(form.amount) || 0, form.frequency))}</strong>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Bill"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Bill" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">Are you sure you want to delete this bill?</p>
        <div className="flex gap-3">
          <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger flex-1 justify-center">Delete</button>
        </div>
      </Modal>
    </div>
  );
}
